<script setup lang="ts">
import { VIcon } from "@app/core";
import { nextTick, onBeforeUnmount, onMounted, ref, watch } from "vue";

import type { QuickSearchItem } from "../api/quickSearch.gql";

import SearchAllItem from "./SearchAllItem.vue";
import SearchResultItem from "./SearchResultItem.vue";
import SearchSkeleton from "./SearchSkeleton.vue";

const { triggerEl, results, total, loading, activeIndex, query } = defineProps<{
  triggerEl: HTMLElement | null
  results: QuickSearchItem[]
  total: number
  loading: boolean
  activeIndex: number | null
  query: string
}>();

const emit = defineEmits<{
  select: [item: QuickSearchItem]
  close: []
  searchAll: []
  "update:activeIndex": [index: number | null]
}>();

const panelStyle = ref<Record<string, string>>({});
const panelRef = ref<HTMLElement | null>(null);

function updatePosition() {
  if (!triggerEl) return;
  const rect = triggerEl.getBoundingClientRect();
  panelStyle.value = {
    position: "fixed",
    top: `${rect.bottom + 6}px`,
    left: `${rect.left}px`,
    width: `${rect.width}px`,
    zIndex: "9999",
  };
}

function handleClickOutside(e: MouseEvent) {
  const path = e.composedPath();
  const insidePanel = panelRef.value && path.includes(panelRef.value);
  const insideTrigger = triggerEl && path.includes(triggerEl);
  if (!insidePanel && !insideTrigger) {
    emit("close");
  }
}

function onResize() {
  updatePosition();
}

onMounted(async () => {
  await nextTick();
  updatePosition();
  window.addEventListener("resize", onResize, { passive: true });
  document.addEventListener("click", handleClickOutside);
});

onBeforeUnmount(() => {
  window.removeEventListener("resize", onResize);
  document.removeEventListener("click", handleClickOutside);
});

watch(
  () => activeIndex,
  async (idx) => {
    if (idx === null) return;
    await nextTick();
    if (idx === -1) {
      panelRef.value?.querySelector<HTMLElement>(".search-all-item")?.scrollIntoView({ block: "nearest" });
    }
    else {
      const items = panelRef.value?.querySelectorAll<HTMLElement>(".result-item");
      items?.[idx]?.scrollIntoView({ block: "nearest" });
    }
  },
);

</script>

<template>
  <Teleport to="body">
    <div
      ref="panelRef"
      :style="panelStyle"
      class="search-panel"
    >
      <SearchSkeleton v-if="loading" />

      <template v-else>
        <SearchAllItem
          :active="activeIndex === -1"
          :query="query"
          :total="total"
          @select="emit('searchAll')"
          @update:active="emit('update:activeIndex', -1)"
        />

        <div
          v-if="!results.length"
          class="flex flex-col items-center justify-center gap-2 py-8 px-4"
        >
          <VIcon
            class="text-secondaryText opacity-40"
            icon="lucide:search-x"
            :size="28"
          />
          <span class="text-sm text-secondaryText">No products found</span>
        </div>

        <div
          v-else
          class="p-2"
        >
          <SearchResultItem
            v-for="(item, i) in results"
            :key="item.id"
            :active="activeIndex === i"
            :index="i"
            :item="item"
            :query="query"
            @select="emit('select', $event)"
            @update:active="emit('update:activeIndex', $event)"
          />
        </div>
      </template>

      <div
        v-if="!loading && total > results.length"
        class="search-panel__footer"
      >
        <VIcon
          class="opacity-50"
          icon="lucide:search"
          :size="13"
        />
        <span>{{ total }} results — refine to narrow down</span>
      </div>
    </div>
  </Teleport>
</template>

<style scoped>
.search-panel {
  background-color: rgb(var(--color-cardBg, 255 255 255) / 1);
  border: 1px solid rgb(var(--color-base-300, 226 232 240) / 1);
  border-radius: 0.75rem;
  box-shadow:
    0 20px 25px -5px rgb(0 0 0 / 0.1),
    0 8px 10px -6px rgb(0 0 0 / 0.1);
  max-height: 480px;
  overflow-y: auto;
  overflow-x: hidden;
  transform-origin: top center;
  animation: panel-in 180ms cubic-bezier(0.23, 1, 0.32, 1) both;
}

.search-panel__footer {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.5rem 1rem 0.625rem;
  border-top: 1px solid rgb(var(--color-base-200, 241 245 249) / 1);
  font-size: 0.6875rem;
  color: rgb(var(--color-secondaryText, 100 116 139) / 1);
}

@keyframes panel-in {
  from {
    opacity: 0;
    transform: scale(0.97) translateY(-4px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

@media (prefers-reduced-motion: reduce) {
  .search-panel {
    animation: panel-fade 120ms ease both;
  }

  @keyframes panel-fade {
    from { opacity: 0; }
    to { opacity: 1; }
  }
}
</style>
